import { CheckCircle2, PlusCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { cn } from "@/lib/utils";

import { MONTHS_PT } from "../constants";
import type { MonthView } from "../types";
import type { TaxClosingPeriodItem } from "@/services/tax-closing-period";
import {
  PERIOD_STATUS_ICON,
  TAX_CLOSING_PERIOD_STATUS,
  TAX_CLOSING_PERIOD_VIEW_KIND,
  type TaxClosingPeriodStatus,
} from "../status";

type Status = TaxClosingPeriodStatus;

interface CardStyle {
  container: string;
  heading: string;
  iconColor: string;
  badgeClass: string;
  badgeLabel: string;
  subtitleClass: string;
  actionLabel: string | null;
  actionClass: string;
  IconComponent: typeof CheckCircle2;
  iconExtraClass: string;
}

const CARD_STYLES: Record<Status, CardStyle> = {
  [TAX_CLOSING_PERIOD_STATUS.RELEASED]: {
    container:
      "bg-green-50/70 border-transparent hover:border-green-500 shadow-sm hover:shadow-md",
    heading: "text-green-900",
    iconColor: "text-green-600",
    badgeClass: "bg-green-200/60 text-green-900",
    badgeLabel: "Status: Liberado",
    subtitleClass: "text-green-800/80",
    actionLabel: "Bloquear período",
    actionClass:
      "bg-white text-green-700 border-green-200 group-hover:bg-green-100",
    IconComponent: PERIOD_STATUS_ICON.RELEASED,
    iconExtraClass: "fill-green-500 text-white",
  },
  [TAX_CLOSING_PERIOD_STATUS.BLOCKED]: {
    container:
      "bg-red-50/60 border-transparent hover:border-red-400 shadow-sm hover:shadow-md",
    heading: "text-red-900",
    iconColor: "text-red-600",
    badgeClass: "bg-red-200/60 text-red-900",
    badgeLabel: "Status: Bloqueado",
    subtitleClass: "text-red-800/80",
    actionLabel: "Liberar período",
    actionClass:
      "bg-white text-red-700 border-red-200 group-hover:bg-red-100",
    IconComponent: PERIOD_STATUS_ICON.BLOCKED,
    iconExtraClass: "",
  },
  [TAX_CLOSING_PERIOD_STATUS.CLOSED]: {
    container:
      "bg-blue-50/60 border-transparent opacity-80 cursor-not-allowed",
    heading: "text-blue-900",
    iconColor: "text-blue-500",
    badgeClass: "bg-blue-200/60 text-blue-900",
    badgeLabel: "Status: Fechado",
    subtitleClass: "text-blue-800/70",
    actionLabel: null,
    actionClass: "",
    IconComponent: PERIOD_STATUS_ICON.CLOSED,
    iconExtraClass: "fill-blue-400 text-white",
  },
};

const SUBTITLE_FALLBACKS: Record<Status, string> = {
  [TAX_CLOSING_PERIOD_STATUS.RELEASED]: "Liberado",
  [TAX_CLOSING_PERIOD_STATUS.BLOCKED]: "Aguardando liberação",
  [TAX_CLOSING_PERIOD_STATUS.CLOSED]: "Período contábil encerrado",
};

interface MonthCardProps {
  view: MonthView;
  isCurrentMonth: boolean;
  isFuture: boolean;
  onClick: (view: MonthView) => void;
}

export function MonthCard({
  view,
  isCurrentMonth,
  isFuture,
  onClick,
}: MonthCardProps) {
  const isPlaceholder = view.kind === "placeholder";

  const renderers: Record<MonthView["kind"], () => JSX.Element> = {
    [TAX_CLOSING_PERIOD_VIEW_KIND.PLACEHOLDER]: () => (
      <PlaceholderCard
        month={(view as Extract<MonthView, { kind: typeof TAX_CLOSING_PERIOD_VIEW_KIND.PLACEHOLDER }>).month}
        year={(view as Extract<MonthView, { kind: typeof TAX_CLOSING_PERIOD_VIEW_KIND.PLACEHOLDER }>).year}
        isCurrentMonth={isCurrentMonth}
        isFuture={isFuture}
        onClick={() => onClick(view)}
      />
    ),
    [TAX_CLOSING_PERIOD_VIEW_KIND.REAL]: () => (
      <RealMonthCard
        data={(view as Extract<MonthView, { kind: typeof TAX_CLOSING_PERIOD_VIEW_KIND.REAL }>).data}
        isCurrentMonth={isCurrentMonth}
        onClick={() => onClick(view)}
      />
    ),
  };

  return renderers[
    isPlaceholder
      ? TAX_CLOSING_PERIOD_VIEW_KIND.PLACEHOLDER
      : TAX_CLOSING_PERIOD_VIEW_KIND.REAL
  ]();
}

// --------------------------------------------------------------------
// Placeholder card (mês nunca cadastrado)
// --------------------------------------------------------------------
interface PlaceholderCardProps {
  month: number;
  year: number;
  isCurrentMonth: boolean;
  isFuture: boolean;
  onClick: () => void;
}

function PlaceholderCard({
  month,
  year,
  isCurrentMonth,
  isFuture,
  onClick,
}: PlaceholderCardProps) {
  const monthName = MONTHS_PT[month - 1];

  // Meses futuros (sem cadastro) são exibidos como "Fechado" — não há ação
  // de liberação/bloqueio disponível ainda. Reaproveita o visual CLOSED.
  if (isFuture) {
    const s = CARD_STYLES[TAX_CLOSING_PERIOD_STATUS.CLOSED];
    const Icon = s.IconComponent;
    return (
      <div
        aria-label={`${monthName} ${year} — ${s.badgeLabel}`}
        aria-disabled
        className={cn(
          "relative w-full text-left p-6 rounded-lg border",
          s.container,
          isCurrentMonth && "ring-2 ring-brand ring-offset-2",
        )}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className={cn("text-lg font-semibold", s.heading)}>{monthName}</h3>
          <div className={s.iconColor}>
            <Icon className={cn("h-5 w-5", s.iconExtraClass)} />
          </div>
        </div>
        <div className="space-y-2">
          <span
            className={cn(
              "inline-block px-2 py-1 text-[10px] font-bold rounded uppercase",
              s.badgeClass,
            )}
          >
            {s.badgeLabel}
          </span>
          <p className={cn("text-xs line-clamp-2", s.subtitleClass)}>
            {SUBTITLE_FALLBACKS.CLOSED}
          </p>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={isFuture}
      onClick={onClick}
      aria-label={`${monthName} ${year} — sem cadastro. Clique para liberar.`}
      className={cn(
        "group flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed transition-all",
        "border-muted-foreground/30 bg-muted/30 text-muted-foreground",
        "hover:border-brand hover:text-brand hover:bg-muted/50",
        "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2",
        isCurrentMonth && "ring-2 ring-brand ring-offset-2",
        isFuture &&
          "opacity-50 cursor-not-allowed hover:border-muted-foreground/30 hover:text-muted-foreground hover:bg-muted/30",
      )}
    >
      <PlusCircle
        className={cn(
          "h-8 w-8 mb-2 transition-transform",
          !isFuture && "group-hover:scale-110",
        )}
      />
      <h3 className="text-lg font-semibold">{monthName}</h3>
      <p className="text-xs mt-1">
        {isFuture ? "Período futuro" : "Clique para iniciar"}
      </p>
    </button>
  );
}

// --------------------------------------------------------------------
// Real card (RELEASED / BLOCKED / CLOSED)
// --------------------------------------------------------------------
interface RealMonthCardProps {
  data: TaxClosingPeriodItem;
  isCurrentMonth: boolean;
  onClick: () => void;
}

function RealMonthCard({ data, isCurrentMonth, onClick }: RealMonthCardProps) {
  const { status, releasedAt, releasedBy, releasedByName, observation } = data;
  const monthName = MONTHS_PT[data.month - 1];
  const releasedDisplay = releasedByName ?? releasedBy;
  const s = CARD_STYLES[status];
  const isDisabled = status === TAX_CLOSING_PERIOD_STATUS.CLOSED;
  const Icon = s.IconComponent;

  // Subtitle resolution: explicit per status > fallback table
  const subtitleByStatus: Record<Status, string> = {
    [TAX_CLOSING_PERIOD_STATUS.RELEASED]:
      releasedAt != null
        ? `Liberado em ${format(parseISO(releasedAt), "dd/MM/yyyy", { locale: ptBR })}`
        : SUBTITLE_FALLBACKS.RELEASED,
    [TAX_CLOSING_PERIOD_STATUS.BLOCKED]: observation ?? SUBTITLE_FALLBACKS.BLOCKED,
    [TAX_CLOSING_PERIOD_STATUS.CLOSED]: SUBTITLE_FALLBACKS.CLOSED,
  };

  const card = (
    <button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      aria-label={`${monthName} ${data.year} — ${s.badgeLabel}`}
      className={cn(
        "group relative w-full text-left p-6 rounded-lg border transition-all",
        s.container,
        !isDisabled &&
          "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2",
        isCurrentMonth && "ring-2 ring-brand ring-offset-2",
      )}
    >
      <div className="flex justify-between items-start mb-4">
        <h3 className={cn("text-lg font-semibold", s.heading)}>{monthName}</h3>
        <div className={s.iconColor}>
          <Icon className={cn("h-5 w-5", s.iconExtraClass)} />
        </div>
      </div>

      <div className="space-y-2">
        <span
          className={cn(
            "inline-block px-2 py-1 text-[10px] font-bold rounded uppercase",
            s.badgeClass,
          )}
        >
          {s.badgeLabel}
        </span>
        <p className={cn("text-xs line-clamp-2", s.subtitleClass)}>
          {subtitleByStatus[status]}
        </p>
      </div>

      {s.actionLabel && (
        <div
          className={cn(
            "mt-4 w-full py-2 text-xs font-bold text-center rounded-md border transition-colors",
            s.actionClass,
          )}
        >
          {s.actionLabel}
        </div>
      )}
    </button>
  );

  // Only RELEASED cards with metadata get a tooltip
  const shouldShowTooltip =
    status === TAX_CLOSING_PERIOD_STATUS.RELEASED &&
    (releasedDisplay != null || observation != null);

  if (!shouldShowTooltip) return card;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{card}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="text-xs leading-relaxed">
          {releasedDisplay && (
            <div>
              <span className="opacity-70">Liberado por:</span>{" "}
              {releasedDisplay}
            </div>
          )}
          {releasedAt && (
            <div>
              <span className="opacity-70">Em:</span>{" "}
              {format(parseISO(releasedAt), "dd/MM/yyyy 'às' HH:mm", {
                locale: ptBR,
              })}
            </div>
          )}
          {observation && <div className="mt-1 italic">"{observation}"</div>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
